import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Button, Divider } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { AddBoxIcon } from '@sharedCR/coreDesignSystem/icons';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import { shouldResetCountyField } from 'app/pages/loanApplication/LoanApplication.utils';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { entitySideDataTableStyles } from 'app/shared/componentsRestyled/common/form/shared/styles';
import _ from 'lodash';

import { IconSize } from 'constants/coreDesignSystem';
import { FeatureFlag } from 'constants/featureFlags';
import { AddressField, DEFAULT_COUNTRY, USCountryOption } from 'constants/loanApplication';
import { housingTypeOptions, stateOptions } from 'constants/options';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useSort from 'hooks/useSort';
import { checkIsAdditionalLoanFlow, checkIsRefinanceFlow } from 'utils/loanApplication';
import { calculatePendingChanges } from 'utils/pendingChanges';

import {
  getLocationsCountiesAction,
  getNonUSLocationsAction,
} from 'store/loanApplications/actions';
import {
  selectAlphabeticallySortedCountiesByStates,
  selectCommonInfoLoanApplication,
  selectIsCountiesExist,
  selectIsNonUSLocationsExist,
  selectNonUSLocationOptions,
  selectSubjectPropertyForResidence,
} from 'store/loanApplications/selectors';

import { BorrowersTestIds } from '../../../../../Borrowers.testIds';
import { pagesPropTypes } from '../../MainContent.constants';

import {
  getAddressConfig,
  getPreviousAddressesTableConfig,
  notFoundTableData,
} from './Address.config';
import { defaultPreviousAddressSortOrder, MAX_PREVIOUS_ADDRESSES_COUNT } from './Address.constants';
import {
  addAnotherAddressStyles,
  boxStyle,
  dividerStyle,
  tableWrapperStyle,
} from './Address.styles';
import {
  createNewPreviousAddressData,
  getPreviousAddressValuesToSave,
  orderPreviousAddresses,
  prepareInitialDeletedFlags,
  preparePreviousAddressesModifiedFields,
} from './Address.utils';
import PreviousAddressForm from './PreviousAddressForm';

const defaultPreviousAddresses = [];
const defaultPendingChanges = {};

const Address = ({
  changedFields,
  isLoading,
  isSaving,
  form,
  validationErrors,
  validateField,
  handleChange,
  entityId,
  section,
  setEntityConfig,
  setInForm,
  setMultipleInForm,
  className,
  setIsLoading,
  entityData: applicationEntityData,
  staticValidateField,
  setEntitiesSideData,
  setInitialEntitiesSideData,
  entitiesSideData,
  initialEntitiesSideData,
  setIsSavingAllowed,
  pendingChanges,
  disabledPendingChangesHighlight,
  newEntities,
  deletedEntities,
  setForm,
  setIsNestedEntityEditActive,
  applicationData,
}) => {
  const dispatch = useDispatch();

  const isMilitaryAddressAllowed = useFeatureToggle(FeatureFlag.MilitaryAddressToStates);

  const counties = useSelector(selectAlphabeticallySortedCountiesByStates);
  const isCountiesExist = useSelector(selectIsCountiesExist);
  const nonUSCountries = useSelector(selectNonUSLocationOptions);
  const isNonUSLocationsExist = useSelector(selectIsNonUSLocationsExist);
  const { loanPurpose } = useSelector(selectCommonInfoLoanApplication);
  const subjectPropertyForResidence = useSelector(selectSubjectPropertyForResidence);

  const [editablePreviousAddressData, setEditablePreviousAddressData] = useState(null);

  const { order, onRequestSort } = useSort(defaultPreviousAddressSortOrder);

  const isRefinanceFlow = checkIsRefinanceFlow(loanPurpose);
  const isAdditionalLoanFlow = checkIsAdditionalLoanFlow(loanPurpose); // TODO: add "&& helocPurpose !== HelocPurposeType.HOME_PURCHASE" when BE will fix isSubjectProperty flag
  const isSubjectPropertyCheckboxVisible = isRefinanceFlow || isAdditionalLoanFlow;
  const formData = form[entityId];
  const isFormDataEmpty = _.isEmpty(formData?.currentResidence);
  const entityData = isFormDataEmpty ? applicationEntityData : formData;
  const isDataEmpty = _.isEmpty(entityData);
  const isDataLoading = isLoading && isDataEmpty;
  const previousAddressesTableData = applicationEntityData?.previousResidences;
  const ownershipType =
    entityData?.currentResidence?.housingType?.value || entityData?.currentResidence?.housingType;
  const isLiveTogetherForTwoYears = _.isNil(formData?.isLiveTogetherForTwoYears)
    ? applicationEntityData?.isLiveTogetherForTwoYears
    : formData?.isLiveTogetherForTwoYears;
  const entitySideData = entitiesSideData[entityId];
  const { previousAddresses } = entitySideData || {};
  const isPreviousAddressEditActive = !!editablePreviousAddressData;
  const isPreviousAddressesExist = !!previousAddresses?.length;
  const isAddButtonDisabled =
    isLoading ||
    isLiveTogetherForTwoYears ||
    previousAddresses?.length >= MAX_PREVIOUS_ADDRESSES_COUNT;
  const isPreviousAddressActionDisabled = isLoading || isLiveTogetherForTwoYears;
  const isNewAddedBorrower = newEntities.includes(entityId);
  const isDeletedBorrower = deletedEntities.includes(entityId);
  const isSubjectPropertyReceivedValue =
    applicationEntityData?.currentResidence?.isSubjectProperty ?? false;
  const primaryBorrowerId = _.find(applicationData, borrower => borrower.isPrimaryBorrower)?.id;
  const formPrimaryBorrowerId = form[primaryBorrowerId]?.id;

  useEffect(() => {
    setIsSavingAllowed(!isPreviousAddressEditActive);
    setIsNestedEntityEditActive(isPreviousAddressEditActive);

    return () => {
      setIsSavingAllowed(true);
    };
  }, [isPreviousAddressEditActive, setIsSavingAllowed, setIsNestedEntityEditActive]);

  useEffect(() => {
    if (!editablePreviousAddressData || editablePreviousAddressData.entityId === entityId) {
      return;
    }

    if (editablePreviousAddressData.isNewlyAdded) {
      setEntitiesSideData(prevState => ({
        ...prevState,
        [editablePreviousAddressData.entityId]: {
          ...prevState[editablePreviousAddressData.entityId],
          previousAddresses: prevState[
            editablePreviousAddressData.entityId
          ].previousAddresses.filter(previousAddress => !previousAddress.isNewlyAdded),
        },
      }));
    }

    setEditablePreviousAddressData(null);
  }, [editablePreviousAddressData, entityId, setEntitiesSideData]);

  useEffect(() => {
    if (!formPrimaryBorrowerId || !isLiveTogetherForTwoYears) {
      return;
    }

    setForm(prevForm => {
      const { currentResidence } = prevForm[formPrimaryBorrowerId];

      return {
        ...prevForm,
        [entityId]: {
          ...prevForm[entityId],
          currentResidence,
        },
      };
    });
  }, [counties, entityId, isLiveTogetherForTwoYears, setForm, formPrimaryBorrowerId]);

  const setExtendedEditablePreviousAddressData = useCallback(
    dataToEdit => {
      setEditablePreviousAddressData({
        ...dataToEdit,
        entityId,
      });
    },
    [entityId],
  );

  const initialPreviousAddressesValuesMap = useMemo(() => {
    if (!initialEntitiesSideData[entityId]?.previousAddresses) {
      return {};
    }

    return _.keyBy(initialEntitiesSideData[entityId].previousAddresses, 'id');
  }, [entityId, initialEntitiesSideData]);

  const previousAddressesPendingChangesData = useMemo(
    () => pendingChanges?.[entityId]?.previousAddresses ?? {},
    [pendingChanges, entityId],
  );

  const pendingChangesCount = calculatePendingChanges(previousAddressesPendingChangesData).count;

  const formPendingChanges = useMemo(() => {
    if (!editablePreviousAddressData || editablePreviousAddressData.isNewlyAdded) {
      return defaultPendingChanges;
    }

    return (
      previousAddressesPendingChangesData[
        editablePreviousAddressData.addressId ?? editablePreviousAddressData.id
      ] ?? {}
    );
  }, [editablePreviousAddressData, previousAddressesPendingChangesData]);

  const orderedAddressesWithPendingChanges = useMemo(() => {
    if (!isPreviousAddressesExist) {
      return defaultPreviousAddresses;
    }

    const orderedAddresses = orderPreviousAddresses({ previousAddresses, order });

    return preparePreviousAddressesModifiedFields({
      addresses: orderedAddresses,
      previousAddressesPendingChangesData,
      initialPreviousAddressesValuesMap,
    });
  }, [
    isPreviousAddressesExist,
    previousAddresses,
    order,
    previousAddressesPendingChangesData,
    initialPreviousAddressesValuesMap,
  ]);

  const correctedPreviousAddressesTableData = isPreviousAddressesExist
    ? orderedAddressesWithPendingChanges
    : notFoundTableData;

  useEffect(() => {
    if (!previousAddressesTableData) {
      return;
    }

    const entitySideData = initialEntitiesSideData[entityId] || {};
    const isPropertyExist = _.has(entitySideData, 'previousAddresses');

    if (isPropertyExist) {
      return;
    }

    const preparedInitialData = prepareInitialDeletedFlags({
      previousAddresses: previousAddressesTableData,
      previousAddressesPendingChangesData,
      isBorrowerDeleted: isNewAddedBorrower,
      isBorrowerNewAdded: isDeletedBorrower,
    });

    setEntitiesSideData(prevState => ({
      ...prevState,
      [entityId]: {
        ...entitySideData,
        previousAddresses: preparedInitialData,
      },
    }));
    setInitialEntitiesSideData(prevState => ({
      ...prevState,
      [entityId]: {
        ...entitySideData,
        previousAddresses: preparedInitialData,
      },
    }));
  }, [
    entitiesSideData,
    entityId,
    initialEntitiesSideData,
    previousAddressesPendingChangesData,
    previousAddressesTableData,
    setEntitiesSideData,
    setInitialEntitiesSideData,
    isNewAddedBorrower,
    isDeletedBorrower,
  ]);

  useEffectSafeStrictMode(() => {
    if (isNonUSLocationsExist) {
      return;
    }

    dispatch(getNonUSLocationsAction()).catch(Error);
  }, [dispatch, isNonUSLocationsExist, setIsLoading]);

  useEffectSafeStrictMode(() => {
    if (isCountiesExist) {
      return;
    }

    setIsLoading(true);

    dispatch(getLocationsCountiesAction())
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [dispatch, isCountiesExist, setIsLoading]);

  const deletePreviousAddress = useCallback(
    id => {
      setEntitiesSideData(prevState => ({
        ...prevState,
        [entityId]: {
          ...prevState[entityId],
          previousAddresses: prevState[entityId].previousAddresses.reduce(
            (acc, previousAddress) => {
              if (previousAddress.id !== id) {
                acc.push(previousAddress);

                return acc;
              }

              if (previousAddress.isAdded) {
                return acc;
              }

              acc.push({ ...previousAddress, isDeleted: true });

              return acc;
            },
            [],
          ),
        },
      }));
      setEditablePreviousAddressData(null);
    },
    [entityId, setEntitiesSideData],
  );

  const previousAddressesTableConfig = useMemo(
    () =>
      getPreviousAddressesTableConfig({
        setEditablePreviousAddressData: setExtendedEditablePreviousAddressData,
        deletePreviousAddress,
        editableRowId: editablePreviousAddressData?.id,
        isPreviousAddressActionDisabled,
      }),
    [
      deletePreviousAddress,
      editablePreviousAddressData?.id,
      setExtendedEditablePreviousAddressData,
      isPreviousAddressActionDisabled,
    ],
  );

  const addNewPreviousAddress = useCallback(() => {
    const previousAddressData = createNewPreviousAddressData();

    setEntitiesSideData(prevState => ({
      ...prevState,
      [entityId]: {
        ...prevState[entityId],
        previousAddresses: [...(prevState[entityId]?.previousAddresses ?? []), previousAddressData],
      },
    }));
    setExtendedEditablePreviousAddressData(previousAddressData);
  }, [entityId, setEntitiesSideData, setExtendedEditablePreviousAddressData]);

  useEffect(() => {
    const shouldResetCounty = shouldResetCountyField({
      counties,
      formState: formData?.currentResidence?.state?.value,
      formCounty: formData?.currentResidence?.county?.value,
    });

    if (!shouldResetCounty) {
      return;
    }

    setInForm(`[${entityId}].currentResidence.county`, null, false);
  }, [
    counties,
    entityId,
    formData?.currentResidence?.county?.value,
    formData?.currentResidence?.state?.value,
    setInForm,
  ]);

  const onChangeIsOutsideUsa = useCallback(
    e => {
      const { name, checked } = e.target;

      const splittedPath = name.split('.');
      const path = `${splittedPath[0]}.${splittedPath[1]}.`;

      setMultipleInForm(
        {
          [`${path}state`]: '',
          [`${path}country`]: '',
          [`${path}zip`]: '',
          [`${path}county`]: '',
        },
        false,
      );
      setInForm(`${path}isOutsideUsa`, checked, true);

      if (!checked) {
        setInForm(`${path}country`, DEFAULT_COUNTRY, false);
      }
    },
    [setInForm, setMultipleInForm],
  );

  const onChangeIsSubjectProperty = useCallback(
    (e, value) => {
      const { name } = e.target;

      setForm(form => {
        const pathToCurrentResidence = `[${entityId}].currentResidence`;

        const { startOfResidence } = _.get(form, pathToCurrentResidence);

        const updatedCurrentResidence = value
          ? { ...subjectPropertyForResidence, startOfResidence }
          : Object.values(AddressField).reduce(
              (acc, fieldName) => {
                acc[fieldName] = isSubjectPropertyReceivedValue ? null : undefined;

                return acc;
              },
              {
                isSubjectProperty: false,
                startOfResidence,
              },
            );

        const { state, county, country, isOutsideUsa, housingType, ...restResidenceFields } =
          updatedCurrentResidence;

        const addressCounties = state ? counties[state] : counties.ALL_COUNTIES;

        const formattedResidenceToSetInForm = value
          ? {
              ...restResidenceFields,
              state: !isOutsideUsa ? stateOptions.find(({ value }) => value === state) : state,
              county: !isOutsideUsa
                ? addressCounties.find(({ value }) => value === county)
                : county,
              country: !isOutsideUsa ? country : USCountryOption,
              housingType: housingTypeOptions.find(({ value }) => value === housingType),
            }
          : updatedCurrentResidence;

        Object.values(AddressField).forEach(fieldName => {
          staticValidateField(
            `[${entityId}].currentResidence[${fieldName}]`,
            formattedResidenceToSetInForm[fieldName],
          );
        });

        return _.set({ ...form }, pathToCurrentResidence, formattedResidenceToSetInForm);
      });

      setInForm(name, value);
      validateField(name, value);
    },
    [
      subjectPropertyForResidence,
      validateField,
      setInForm,
      setForm,
      entityId,
      staticValidateField,
      counties,
      isSubjectPropertyReceivedValue,
    ],
  );

  const config = useMemo(() => {
    const addressState = entityData?.currentResidence?.state?.value;
    const addressCounties = addressState ? counties[addressState] : counties.ALL_COUNTIES;
    const mailingAddressState = entityData?.mailingAddress?.state?.value;
    const mailingAddressCounties = mailingAddressState
      ? counties[mailingAddressState]
      : counties.ALL_COUNTIES;
    const isSubjectProperty = entityData?.currentResidence?.isSubjectProperty;
    const isSubjectPropertyForPrimaryBorrower = isSubjectProperty && entityData?.isPrimaryBorrower;

    return getAddressConfig({
      entityId,
      section,
      addressCounties,
      mailingAddressCounties,
      ownershipType,
      hasRelativesPartRent: entityData?.currentResidence?.hasRelativesPartRent,
      isSubjectPropertyCheckboxVisible,
      isLiveTogetherForTwoYears,
      isSubjectProperty,
      addressIsOutsideUsa: entityData?.currentResidence?.isOutsideUsa,
      nonUSCountries,
      isMilitaryAddressAllowed,
      isSubjectPropertyForPrimaryBorrower,
      isMailingAddressTheSameAsCurrentAddress: entityData?.isMailingAddressTheSameAsCurrentAddress,
      mailingAddressIsOutsideUsa: entityData?.mailingAddress?.isOutsideUsa,
      onChangeIsOutsideUsa,
      onChangeIsSubjectProperty,
    });
  }, [
    isLiveTogetherForTwoYears,
    entityData?.currentResidence?.state?.value,
    entityData?.currentResidence?.isSubjectProperty,
    entityData?.currentResidence?.hasRelativesPartRent,
    entityData?.currentResidence?.isOutsideUsa,
    entityData?.mailingAddress?.state?.value,
    entityData?.mailingAddress?.isOutsideUsa,
    entityData?.isPrimaryBorrower,
    entityData?.isMailingAddressTheSameAsCurrentAddress,
    counties,
    entityId,
    section,
    ownershipType,
    isSubjectPropertyCheckboxVisible,
    nonUSCountries,
    isMilitaryAddressAllowed,
    onChangeIsOutsideUsa,
    onChangeIsSubjectProperty,
  ]);

  useEffect(() => {
    if (!isCountiesExist || !isNonUSLocationsExist || !config) {
      return;
    }

    setEntityConfig(config);
  }, [config, setEntityConfig, isCountiesExist, isNonUSLocationsExist]);

  const cancelForm = useCallback(
    data => {
      if (data.isNewlyAdded) {
        setEntitiesSideData(prevState => ({
          ...prevState,
          [entityId]: {
            ...prevState[entityId],
            previousAddresses: prevState[entityId].previousAddresses.filter(
              ({ id }) => id !== data.id,
            ),
          },
        }));
      }

      setEditablePreviousAddressData(null);
    },
    [entityId, setEntitiesSideData],
  );

  const saveForm = useCallback(
    ({ updatedData, initialData }) => {
      const dataToSave = getPreviousAddressValuesToSave({ updatedData, initialData });

      setEntitiesSideData(prevState => ({
        ...prevState,
        [entityId]: {
          ...prevState[entityId],
          previousAddresses: prevState[entityId].previousAddresses.map(previousAddress =>
            previousAddress.id === dataToSave.id ? dataToSave : previousAddress,
          ),
        },
      }));
      setEditablePreviousAddressData(null);
    },
    [entityId, setEntitiesSideData],
  );

  const additionalRowContent = editablePreviousAddressData ? (
    <PreviousAddressForm
      cancelForm={cancelForm}
      deletePreviousAddress={deletePreviousAddress}
      isLiveTogetherForTwoYears={isLiveTogetherForTwoYears}
      isLoading={isLoading}
      isRefinanceFlow={isRefinanceFlow}
      pendingChanges={formPendingChanges}
      previousAddressData={editablePreviousAddressData}
      saveForm={saveForm}
    />
  ) : null;

  if (!config) {
    return null;
  }

  return (
    <Box className={className}>
      <Box sx={boxStyle}>
        <FormGenerator
          isNestedForm
          changedFields={changedFields}
          config={config}
          disabledPendingChangesHighlight={disabledPendingChangesHighlight}
          form={form}
          formName={FormNames.BORROWER_ADDRESS}
          handleChange={handleChange}
          isLoading={isSaving || isLoading}
          numberOfColumns={4}
          pendingChanges={pendingChanges}
          setInForm={setInForm}
          staticValidateField={staticValidateField}
          validateField={validateField}
          validationErrors={validationErrors}
        />
      </Box>

      <Divider sx={dividerStyle} />

      <TableCDS
        isHideTableWhenEmpty
        additionalRowContent={additionalRowContent}
        additionalRowContentRowId={editablePreviousAddressData?.id}
        config={previousAddressesTableConfig}
        customStyles={entitySideDataTableStyles}
        data={correctedPreviousAddressesTableData}
        isCustomDataDefinition={!isPreviousAddressesExist}
        isLoading={isDataLoading}
        isSortDisabled={isPreviousAddressEditActive}
        order={order}
        pendingChangesCount={isNewAddedBorrower || isDeletedBorrower ? 0 : pendingChangesCount}
        title="Previous Addresses"
        wrapperStyle={tableWrapperStyle}
        onRequestSort={onRequestSort}
      />

      {!isDataLoading && (
        <Box sx={addAnotherAddressStyles}>
          <Button
            data-testid={BorrowersTestIds.PREVIOUS_ADDRESSES_ADD_BUTTON}
            disabled={isAddButtonDisabled || isPreviousAddressEditActive || isLoading}
            size="medium"
            startIcon={<AddBoxIcon size={IconSize[20]} />}
            variant="outlined"
            onClick={addNewPreviousAddress}
          >
            Add Another Address
          </Button>
        </Box>
      )}
    </Box>
  );
};

Address.propTypes = pagesPropTypes;

export default memo(Address);
